import { memo } from 'react';
import { useNotify } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { track } from '@hotjar/webapp/domains/common/tracking/segment';
import { useHotkeys } from 'react-hotkeys-hook';
import { useRealTimeStore } from '../store/realTimeStore';
import type { Recording } from '../types';
import { getGroupDataForRecording, trackClick } from '../utils/tracking';

const EXTRA_PAGE_BUFFER_MS = 100;
const MIN_DURATION_TO_JUMP_TO_START_MS = 1000;

export enum EventsBinding {
  PLAY_PAUSE = 'space, k',
  PLAYBACK_SPEED = 'i',
  SKIP_ALL_PAUSES = 'o',
  REPLAY = 'r',
  REWIND = 'left',
  SKIP = 'right',
  GO_TO_PREVIOUS_RECORDING = 'shift+p',
  GO_TO_NEXT_RECORDING = 'shift+n',
  GO_TO_PREVIOUS_CHAPTER = 'shift+left',
  GO_TO_NEXT_CHAPTER = 'shift+right',
  SIDEPANEL_TOGGLE = 'h',
  SHOW_SHORTCUTS_LEGEND = 'shift+/, /, ?',
  AUTOPLAY_TOGGLE = 'p',
  ADD_COMMENT = 'c',
  SHARE = 's',
  SKIP_CURRENT_PAUSE = 's',
  DELETE_RECORDING = 'd',
}

export interface Props {
  recording: Recording;
  hasNextRecording: boolean;
  hasPreviousRecording: boolean;
  isNewHighlightsOn: boolean;
  isTokenShared: boolean;
  isReadOnly: boolean;
  isSkippingAllPauses: boolean;
  skipPauses: boolean;
  jumpTo: (targetTime: number) => void;
  onSkipCurrentInactivity: () => void;
  onPlayPauseToggle: (event: KeyboardEvent) => void;
  onPlaybackSpeedToggle: () => void;
  onExpandCollapseToggle: () => void;
  onSkipPausesToggle: () => void;
  onReplay: () => void;
  onForward: (milliseconds: number) => void;
  onRewind: (milliseconds: number) => void;
  onNext: () => void;
  onPrevious: () => void;
  onNewComment: () => void;
  onDelete: () => void;
  onAutoplayToggle: () => void;
  onShortcutsModalToggle: () => void;
}

const trackModalOpen = (recording: Recording) => {
  const trackingData = {
    ...getGroupDataForRecording(recording),
    modal_name: 'playback_shortcuts_legend',
    modal_version: 'shortcuts_legend',
    state_event_was_triggered_on: 'playback.player',
    ui_position: 'playback_used-keyboard-shortcut',
  };

  track('Modal Loaded', trackingData);
};

const UnMemoKeyboardShortcutEvents = ({
  recording,
  hasNextRecording,
  hasPreviousRecording,
  isNewHighlightsOn,
  isTokenShared,
  isReadOnly,
  isSkippingAllPauses,
  skipPauses,
  jumpTo,
  onSkipCurrentInactivity,
  onPlayPauseToggle,
  onPlaybackSpeedToggle,
  onSkipPausesToggle,
  onExpandCollapseToggle,
  onReplay,
  onForward,
  onRewind,
  onNext,
  onPrevious,
  onNewComment,
  onDelete,
  onAutoplayToggle,
  onShortcutsModalToggle,
}: Props): JSX.Element | null => {
  const { showToast } = useNotify();
  const { t } = useTranslation();

  // Play/pause recording
  useHotkeys(
    EventsBinding.PLAY_PAUSE,
    (event) => {
      /*
        When the spacebar is pressed, it triggers the click
        on the last focused element.
        This can produce unexpected behavior.
      */
      if (
        document.activeElement instanceof HTMLElement &&
        document.activeElement !== document.body
      ) {
        document.activeElement.blur();
      }
      onPlayPauseToggle(event);
    },
    {},
    [onPlayPauseToggle],
  );

  // Toggle speeds.
  useHotkeys(
    EventsBinding.PLAYBACK_SPEED,
    () => {
      onPlaybackSpeedToggle();
    },
    {},
    [onPlaybackSpeedToggle],
  );

  // Toggle skip pauses.
  useHotkeys(
    EventsBinding.SKIP_ALL_PAUSES,
    () => {
      onSkipPausesToggle();
      trackClick(recording, {
        element_name: `playback_toggled_skip_inactivity_${skipPauses ? 'ON' : 'OFF'}`,
        state_event_was_triggered_on: 'playback.player_settings',
        ui_position: 'used_keyboard_shortcut',
      });

      showToast({
        kind: 'success',
        text: skipPauses ? 'Skip all inactivity enabled' : 'Skip all inactivity disabled',
      });
    },
    [onSkipPausesToggle, recording],
  );

  // Restart recording
  useHotkeys(
    EventsBinding.REPLAY,
    () => {
      onReplay();
    },
    {},
    [onReplay],
  );

  // Move 10s backward
  useHotkeys(
    EventsBinding.REWIND,
    () => {
      trackClick(recording, {
        element_name: 'playback_jumped_backward',
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: 'used-keyboard-shortcut_10s-rewind',
      });

      onRewind(10000);
    },
    {},
    [onRewind, recording],
  );

  // Move 10s forward
  useHotkeys(
    EventsBinding.SKIP,
    () => {
      trackClick(recording, {
        element_name: 'playback_jumped_forward',
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: 'used-keyboard-shortcut_10s-fast-forward',
      });

      onForward(10000);
    },
    {},
    [onForward, recording],
  );

  // Toggle sidepanel.
  useHotkeys(
    EventsBinding.SIDEPANEL_TOGGLE,
    () => {
      onExpandCollapseToggle();
    },
    {},
    [onExpandCollapseToggle],
  );

  // Show shortcut guide.
  useHotkeys(
    EventsBinding.SHOW_SHORTCUTS_LEGEND,
    () => {
      onShortcutsModalToggle();
      trackModalOpen(recording);
    },
    {},
    [onShortcutsModalToggle, recording],
  );

  // Page chapters navigation backward
  useHotkeys(
    EventsBinding.GO_TO_PREVIOUS_CHAPTER,
    () => {
      const realTimeStoreState = useRealTimeStore.getState();
      if (
        realTimeStoreState.sectionInfoList[realTimeStoreState.sectionInfoList.length - 1]
          .pageIndex === 0
      ) {
        showToast({
          kind: 'info',
          text: t('common:player.shortcuts.notification-messages.only-one-page'),
        });

        return;
      }

      let targetPageIndex = realTimeStoreState.currentPageIndex - 1;
      if (
        targetPageIndex === -1 &&
        realTimeStoreState.currentTime > MIN_DURATION_TO_JUMP_TO_START_MS
      ) {
        // We are already at the first page
        // We are beyond the first second
        // => We should still do the jump
        targetPageIndex = 0;
      }
      const targetSectionInfo = realTimeStoreState.sectionInfoList.find(
        (sectionInfo) => sectionInfo.pageIndex === targetPageIndex,
      );

      if (!targetSectionInfo) {
        showToast({
          kind: 'info',
          text: t('common:player.shortcuts.notification-messages.no-prev-page'),
        });

        return;
      }

      const timeToJumpTo = targetSectionInfo.startTimeInMs - EXTRA_PAGE_BUFFER_MS;
      jumpTo(timeToJumpTo);

      trackClick(recording, {
        element_name: `playback_jumped_backward`,
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: `used-keyboard-shortcut_previous-page-chapter`,
      });
    },
    {},
    [jumpTo, recording],
  );

  // Page chapters navigation forward
  useHotkeys(
    EventsBinding.GO_TO_NEXT_CHAPTER,
    () => {
      const realTimeStoreState = useRealTimeStore.getState();
      if (
        realTimeStoreState.sectionInfoList[realTimeStoreState.sectionInfoList.length - 1]
          .pageIndex === 0
      ) {
        showToast({
          kind: 'info',
          text: t('common:player.shortcuts.notification-messages.only-one-page'),
        });

        return;
      }

      const targetPageIndex = realTimeStoreState.currentPageIndex + 1;
      const targetSectionInfo = realTimeStoreState.sectionInfoList.find(
        (sectionInfo) => sectionInfo.pageIndex === targetPageIndex,
      );

      if (!targetSectionInfo) {
        showToast({
          kind: 'info',
          text: t('common:player.shortcuts.notification-messages.no-next-page'),
        });

        return;
      }

      const timeToJumpTo = targetSectionInfo.startTimeInMs + EXTRA_PAGE_BUFFER_MS;
      jumpTo(timeToJumpTo);

      trackClick(recording, {
        element_name: `playback_jumped_forward`,
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: `used-keyboard-shortcut_next-page-chapter`,
      });
    },
    {},
    [jumpTo, recording],
  );

  /**
   * Bindings only available when logged in.
   */
  // Go to previous recording.
  useHotkeys(
    EventsBinding.GO_TO_PREVIOUS_RECORDING,
    () => {
      if (isTokenShared) {
        return;
      }

      if (hasPreviousRecording) {
        onPrevious();
      } else {
        showToast({
          kind: 'info',
          text: t('common:player.shortcuts.notification-messages.no-prev-recording'),
        });
      }

      trackClick(recording, {
        element_name: 'playback_went_to_previous_recording',
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: 'used_keyboard_shortcut-previous_recording',
      });
    },
    { enabled: !isTokenShared },
    [hasPreviousRecording, onPrevious, recording, isTokenShared],
  );

  // Go to next recording.
  useHotkeys(
    EventsBinding.GO_TO_NEXT_RECORDING,
    () => {
      if (isTokenShared) {
        return;
      }

      if (hasNextRecording) {
        onNext();
      } else {
        showToast({
          kind: 'info',
          text: t('common:player.shortcuts.notification-messages.no-next-recording'),
        });
      }

      trackClick(recording, {
        element_name: 'playback_went_to_next_recording',
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: 'used_keyboard_shortcut-next_recording',
      });
    },
    { enabled: !isTokenShared },
    [hasNextRecording, onNext, recording, isTokenShared],
  );

  // Toggle autoplay.
  useHotkeys(
    EventsBinding.AUTOPLAY_TOGGLE,
    () => {
      onAutoplayToggle();
    },
    { enabled: !isTokenShared },
    [onAutoplayToggle, isTokenShared],
  );

  // Add comment.
  useHotkeys(
    EventsBinding.ADD_COMMENT,
    () => {
      if (isTokenShared) {
        return;
      }
      onNewComment();

      if (isNewHighlightsOn) {
        trackClick(recording, {
          element_name: 'player_keyboard_shortcut',
          state_event_was_triggered_on: 'playback.player',
          ui_position: 'used_keyboard_shortcut-save_clip_highlights',
          url_event_was_triggered_on: window.location.href,
        });
      }
    },
    { enabled: !isTokenShared, keyup: true },
    [onNewComment, isTokenShared],
  );

  /**
   * Bindings only available when logged-in and having write / admin access.
   */

  // Delete recording.
  useHotkeys(
    EventsBinding.DELETE_RECORDING,
    () => {
      if (isTokenShared && isReadOnly) {
        return;
      }
      onDelete();
    },
    { enabled: !isTokenShared || !isReadOnly },
    [onDelete, isTokenShared, isReadOnly],
  );

  // Show skip current pauses.
  useHotkeys(
    EventsBinding.SKIP_CURRENT_PAUSE,
    () => {
      onSkipCurrentInactivity();
      trackClick(recording, {
        element_name: 'skip_single_period_of_inactivity',
        state_event_was_triggered_on: 'playback.player_navigation',
        ui_position: 'used_keyboard_shortcut',
      });
    },
    { enabled: !isSkippingAllPauses },
    [onSkipCurrentInactivity, recording],
  );

  return null;
};

export const KeyboardShortcutEvents = memo(UnMemoKeyboardShortcutEvents);
